import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, Subject } from 'rxjs';

import { CreateNote } from '@shared-models';

import { IBillyNotesToGraphQ } from 'src/app/@interfaces/Billy/billy.interface';
import { INotas, INotasGrap } from 'src/app/@interfaces/notas.interface';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';

import { AlertService } from '@shared-services';
import { DianNoteService } from '@shared-services';

@Injectable({
  providedIn: 'root',
})
export class CreateNoteService {
  dialog = inject(MatDialog);
  private quotationService = inject(QuotationsService);
  private destroyRef = inject(DestroyRef);
  private spinnerService = inject(NgxSpinnerService);
  private alertService = inject(AlertService);
  private translate = inject(TranslateService);
  private dianNoteService = inject(DianNoteService);

  private readonly noteCreatedSource = new Subject<void>();
  /**
   * Emits once the note exists in the database, and again once the electronic
   * flow that follows it has finished. Screens that stay open across a creation
   * listen to this to reload what they show: `dialog.closeAll()` below only
   * reaches MatDialog, so a modal opened through the uplift ModalService keeps
   * stale data otherwise.
   */
  readonly noteCreated$ = this.noteCreatedSource.asObservable();

  createNote(
    noteToCreate: INotas,
    userId: number,
    sendDian = false,
    billyNote?: IBillyNotesToGraphQ,
  ) {
    const noteParams = this.buildNoteParams(noteToCreate, userId);
    this.quotationService
      .createNote(noteParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (result) => {
          this.spinnerService.hide();
          if (!result) {
            this.showErrorAlert();
            return;
          }
          // Announced before the success alert so the screen behind it reloads
          // whether the user accepts the alert or dismisses it.
          this.noteCreatedSource.next();
          await this.handleSuccessfulCreation(result, sendDian, billyNote!);
        },
        error: (err) => {
          this.spinnerService.hide();
          console.error('Error creating note:', err);
          this.showErrorAlert(err);
        },
      });
  }

  private showErrorAlert(message?: string) {
    this.alertService.open({
      title: this.translate.instant('global.notes.error'),
      message: message
        ? message
        : this.translate.instant('Something went wrong'),
      type: 'error',
      showClose: true,
    });
  }

  private buildNoteParams(note: INotas, userId: number): CreateNote {
    const {
      note_fact,
      tipo_nota,
      concepto,
      total,
      createAt,
      dueDate,
      valores,
    } = note;

    const {
      id_warehouse,
      id_articulo,
      codigo,
      cantidad,
      precio,
      subtotal,
      descuento,
    } = valores[0];

    return {
      note_fact,
      id_warehouse,
      id_articulo,
      codigo,
      cantidad,
      tipo_nota,
      concepto,
      precio,
      subtotal,
      descuento,
      total,
      createAt,
      dueDate,
      fromLot: false,
      id_user: userId,
    };
  }

  private async handleSuccessfulCreation(
    result: INotasGrap,
    sendDian: boolean,
    billyNote?: IBillyNotesToGraphQ,
  ) {
    try {
      this.dialog.closeAll();
      this.alertService
        .open({
          title: this.translate.instant('global.notes.success-title'),
          message: this.translate.instant('global.notes.success-description'),
          type: 'success',
          acceptText: this.translate.instant('Next'),
        })
        .subscribe({
          accept: async () => {
            if (billyNote) {
              this.saveBillyNote(result, billyNote);
            } else if (sendDian) {
              try {
                await this.dianNoteService.sendDian(result.id_nota!);
              } finally {
                // The note carries its CUDE now (or the send failed and it does
                // not) — either way what the open screen shows is out of date.
                this.noteCreatedSource.next();
              }
            }
          },
        });
    } catch (error) {
      this.showErrorAlert(
        this.translate.instant('global.notes.after-creation-error'),
      );
      throw error;
    }
  }

  private saveBillyNote(result: INotasGrap, billyNote: IBillyNotesToGraphQ) {
    this.quotationService
      .setBillyNote(
        result.id_nota!.toString(),
        billyNote.data.id,
        billyNote.data.attributes.creditNoteNumber,
        false,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // The note carries its Billy number now (or the save failed and it does
        // not) — either way what the open screen shows is out of date.
        finalize(() => this.noteCreatedSource.next()),
      )
      .subscribe();
  }
}
